import { Injectable, inject, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { BrandingService } from '../services/branding.service';

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noIndex?: boolean;
  canonical?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private meta = inject(Meta);
  private title = inject(Title);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private brandingService = inject(BrandingService);

  private defaultSeo: SeoData = {
    title: 'RMA Property Manager - Rental Management System',
    description: 'Comprehensive property rental management system for landlords, tenants, and property managers. Streamline rent collection, maintenance requests, lease management, and tenant communications in one powerful platform.',
    keywords: 'property management, rental management, landlord software, tenant portal, rent collection, lease management, property maintenance, rental properties, real estate management',
    image: '/assets/images/og-image.jpg',
    url: 'https://www.rmahousing.com',
    type: 'website',
    author: 'RMA Property Management'
  };

  constructor() {
    // Listen to route changes to update canonical URLs
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateCanonicalUrl(event.url);
    });
  }

  updateSeoData(seoData: SeoData): void {
    const branding = this.brandingService.branding();
    const data = { ...this.defaultSeo, ...seoData };

    // Update title
    if (data.title) {
      this.title.setTitle(data.title);
    }

    // Update meta tags
    this.updateMetaTag('name', 'description', data.description);
    this.updateMetaTag('name', 'keywords', data.keywords);
    this.updateMetaTag('name', 'author', data.author);
    this.updateMetaTag('name', 'robots', data.noIndex ? 'noindex, nofollow' : 'index, follow');

    // Update Open Graph tags
    this.updateMetaTag('property', 'og:title', data.title);
    this.updateMetaTag('property', 'og:description', data.description);
    this.updateMetaTag('property', 'og:image', data.image);
    this.updateMetaTag('property', 'og:url', data.url || this.getCurrentUrl());
    this.updateMetaTag('property', 'og:type', data.type);
    this.updateMetaTag('property', 'og:site_name', branding.companyName);

    // Update Twitter Card tags
    this.updateMetaTag('name', 'twitter:card', 'summary_large_image');
    this.updateMetaTag('name', 'twitter:title', data.title);
    this.updateMetaTag('name', 'twitter:description', data.description);
    this.updateMetaTag('name', 'twitter:image', data.image);

    // Update article-specific tags if type is article
    if (data.type === 'article') {
      this.updateMetaTag('property', 'article:author', data.author);
      this.updateMetaTag('property', 'article:published_time', data.publishedTime);
      this.updateMetaTag('property', 'article:modified_time', data.modifiedTime);
      this.updateMetaTag('property', 'article:section', data.section);
      
      if (data.tags) {
        // Remove existing article:tag meta tags
        this.meta.removeTag('property="article:tag"');
        // Add new tags
        data.tags.forEach(tag => {
          this.meta.addTag({ property: 'article:tag', content: tag });
        });
      }
    }

    // Update canonical URL
    if (data.canonical) {
      this.updateCanonicalUrl(data.canonical);
    }
  }

  addStructuredData(data: any): void {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    script.id = 'structured-data';
    
    // Remove existing structured data
    const existing = this.document.getElementById('structured-data');
    if (existing) {
      existing.remove();
    }
    
    this.document.head.appendChild(script);
  }

  generateBreadcrumbStructuredData(breadcrumbs: Array<{name: string, url: string}>): any {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  }

  generateWebsiteStructuredData(): any {
    const branding = this.brandingService.branding();
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": branding.companyName,
      "url": "https://www.rmahousing.com",
      "description": this.defaultSeo.description,
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.rmahousing.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };
  }

  generateOrganizationStructuredData(): any {
    const branding = this.brandingService.branding();
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": branding.companyName,
      "url": "https://www.rmahousing.com",
      "logo": branding.logoUrl || "https://www.rmahousing.com/assets/images/logo.png",
      "description": this.defaultSeo.description,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-555-0123",
        "contactType": "customer service"
      },
      "sameAs": [
        "https://www.facebook.com/rmapropertymanager",
        "https://www.twitter.com/rmapropertymanager",
        "https://www.linkedin.com/company/rmapropertymanager"
      ]
    };
  }

  private updateMetaTag(attrSelector: string, attrName: string, content?: string): void {
    if (content) {
      if (this.meta.getTag(`${attrSelector}="${attrName}"`)) {
        this.meta.updateTag({ [attrSelector]: attrName, content });
      } else {
        this.meta.addTag({ [attrSelector]: attrName, content });
      }
    }
  }

  private updateCanonicalUrl(url: string): void {
    const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseUrl()}${url}`;
    
    if (canonical) {
      canonical.href = fullUrl;
    } else {
      const link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl;
      this.document.head.appendChild(link);
    }
  }

  private getCurrentUrl(): string {
    return `${this.getBaseUrl()}${this.router.url}`;
  }

  private getBaseUrl(): string {
    return `${this.document.location.protocol}//${this.document.location.host}`;
  }
}
